import { useRef, MouseEvent, Fragment } from 'react'

import Spinner from '~/components/Spinner'
import { Link } from '~/components/Link'
import { logEvent } from '~/utils/analytics'

import { ExternalLink } from '../ExternalLink'

import { ButtonProps, ButtonSize } from './types'

// TODO:nextjs Link from next may not support all the props passed in here
export default function Button({
  children,
  className,
  disabled,
  download,
  gaEvents,
  href,
  locale,
  isAutoWidth,
  isFullWidth,
  isLoading,
  isWide,
  link = 'internal',
  noPadding,
  onClick,
  size,
  state,
  stopPropagation,
  spinnerColor = 'white',
  style,
  type = 'button',
  rel,
  disableTransition,
  testId,
}: ButtonProps): React.ReactElement {
  const ref = useRef<HTMLButtonElement & HTMLAnchorElement>(null)

  const handleOnClick = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>): void => {
    if (stopPropagation) {
      e.stopPropagation()
    }

    gaEvents?.forEach((event) => logEvent(event))
    onClick?.(e)
  }

  const sharedAttrs = {
    className:
      'flex items-center justify-center ' +
      'rounded focus:outline-none tap-appearance-none ' +
      'font-medium ' +
      `${!disableTransition ? 'transition transform active:scale-95 ' : ''}` +
      `${disabled || isLoading ? 'cursor-not-allowed opacity-50 scale-100' : ''} ` +
      `${btnSizeClass(size, isFullWidth, isAutoWidth, isWide, noPadding)} ${className || ''}`,

    onClick: handleOnClick,
    onMouseUp: () => ref.current?.blur(),
    onTouchEnd: () => ref.current?.blur(),
    locale,
    ref,
    style,
    'data-test-id': testId,
  }

  const to = disabled ? '#' : href ?? '/'

  function renderContent(): React.ReactElement {
    if (isLoading) {
      return <Spinner color={spinnerColor} size={size} />
    } else {
      return <Fragment>{children}</Fragment>
    }
  }

  if (download && href) {
    return (
      <a download={download} href={href} rel={rel} {...sharedAttrs}>
        {renderContent()}
      </a>
    )
  }

  if (link === 'internal' && href) {
    if (state) {
      const queryString = Object.keys(state)
        .map((key) => `${key}=${state[key]}`)
        .join('&')

      // TODO: This will break location state. We need to use query params with Next Link.
      // Not tested yet.
      //   -- https://nextjs.org/docs/api-reference/next/link
      //   -- https://stackoverflow.com/questions/57978833/can-i-add-a-property-or-state-into-next-js-router-like-react-route
      return (
        <Link as={to} rel={rel} to={`${to}${queryString}`} {...sharedAttrs}>
          {renderContent()}
        </Link>
      )
    } else {
      return (
        <Link rel={rel} to={to} {...sharedAttrs}>
          {renderContent()}
        </Link>
      )
    }
  }

  if (link === 'external' && to) {
    return (
      <ExternalLink href={to} rel={rel} {...sharedAttrs}>
        {renderContent()}
      </ExternalLink>
    )
  }

  return (
    <button disabled={disabled} type={type} {...sharedAttrs}>
      {renderContent()}
    </button>
  )
}

export function btnSizeClass(
  size: ButtonSize,
  fullWidth?: boolean,
  autoWidth?: boolean,
  isWide?: boolean,
  noPadding?: boolean,
): string {
  let px
  let padding
  let result = ''

  switch (size) {
    case 'huge':
      px = isWide ? 'px-16' : 'px-8'
      padding = noPadding ? '' : `p-5 ${px}`
      result = `${padding} space-x-4 text-btn-lg tracking-btn-wide `
      break

    case 'large':
      px = isWide ? 'px-16' : 'px-8'
      padding = noPadding ? '' : `p-5 ${px}`
      result = `${padding} space-x-4 text-btn-md tracking-btn-wide `
      break

    case 'medium':
      px = isWide ? 'px-12' : 'px-6'
      padding = noPadding ? '' : `p-4 ${px}`
      result = `${padding} space-x-3 text-btn-md tracking-btn-wide `
      break

    case 'google':
      result = `space-x-2 text-btn-sm tracking-btn-tight `
      break
    case 'small':
      px = isWide ? 'px-10' : 'px-5'
      padding = noPadding ? '' : `p-3 ${px}`
      result = `${padding} space-x-2 text-btn-sm tracking-btn-tight `
      break

    case 'x-small':
      px = isWide ? 'px-8' : 'px-4'
      padding = noPadding ? '' : `p-2 ${px}`
      result = `${padding} space-x-2 text-btn-sm tracking-btn-tight `
      break

    case 'xx-small':
      px = isWide ? 'px-4' : 'px-2'
      padding = noPadding ? '' : `p-1 ${px}`
      result = `${padding} space-x-2 text-btn-sm tracking-btn-tight `
      break

    default:
      return ''
  }

  if (fullWidth) {
    return result.concat('w-full')
  }

  if (autoWidth) {
    return result.concat('w-auto')
  }

  return result.concat('w-max')
}
